import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import { exportToXLS, exportToCSV, getCellValue } from "../utils/exportHelper";

const COLUMN_CATEGORIES = [
    {
        id: "personal",
        titleKey: "Pages.UsersControl.export.category.personal",
        defaultTitle: "المعلومات الشخصية للاتصال",
        icon: "fad fa-address-card",
        columns: [
            { key: "Name", labelKey: "Pages.UsersControl.advancedSearch.name", icon: "fal fa-user", defaultChecked: true },
            { key: "Email", labelKey: "Pages.UsersControl.advancedSearch.email", icon: "fal fa-envelope", defaultChecked: true },
            { key: "Phone", labelKey: "Pages.UsersControl.advancedSearch.phone", icon: "fal fa-phone", defaultChecked: true },
            { key: "Country", labelKey: "Pages.UsersControl.advancedSearch.Country.title", icon: "fal fa-globe-asia", defaultChecked: true }
        ]
    },
    {
        id: "account",
        titleKey: "Pages.UsersControl.export.category.account",
        defaultTitle: "بيانات ومستوى الحساب",
        icon: "fad fa-user-shield",
        columns: [
            { key: "Username", labelKey: "Pages.UsersControl.advancedSearch.username", icon: "fal fa-at", defaultChecked: true },
            { key: "UserType", labelKey: "Pages.UsersControl.UserType.title", icon: "fal fa-user-tag", defaultChecked: true },
            { key: "School", labelKey: "Pages.UsersControl.advancedSearch.School", icon: "fal fa-school", defaultChecked: true },
            { key: "Status", labelKey: "Pages.UsersControl.advancedSearch.IsActive", icon: "fal fa-shield-check", defaultChecked: true }
        ]
    },
    {
        id: "activity",
        titleKey: "Pages.UsersControl.export.category.activity",
        defaultTitle: "سجل تواريخ النشاط",
        icon: "fad fa-analytics",
        columns: [
            { key: "LastLogin", labelKey: "Pages.Profile.LastLogin.title", icon: "fal fa-history", defaultChecked: false },
            { key: "ActivateUntilDate", labelKey: "Pages.Profile.ActivateUntilDate.title", icon: "fal fa-calendar-star", defaultChecked: false },
            { key: "CreationDate", labelKey: "Pages.Lessons.CreationDate", icon: "fal fa-calendar-plus", defaultChecked: false }
        ]
    }
];

const ALL_COLUMNS = COLUMN_CATEGORIES.flatMap(cat => cat.columns);

const DownloadModal = ({ show, onClose, data, t }) => {
    const [selectedKeys, setSelectedKeys] = useState(
        ALL_COLUMNS.filter(col => col.defaultChecked).map(col => col.key)
    );
    const [format, setFormat] = useState("xls"); // xls, csv, pdf

    if (!show) return null;

    const handleCheckboxChange = (key) => {
        if (selectedKeys.includes(key)) {
            setSelectedKeys(selectedKeys.filter(k => k !== key));
        } else {
            setSelectedKeys([...selectedKeys, key]);
        }
    };

    const handleSelectAll = () => {
        if (selectedKeys.length === ALL_COLUMNS.length) {
            setSelectedKeys([]);
        } else {
            setSelectedKeys(ALL_COLUMNS.map(col => col.key));
        }
    };

    const handleDownload = () => {
        if (selectedKeys.length === 0) {
            alert(t("Pages.Register.error_AllInformationIsRequired_toast.Title"));
            return;
        }

        const activeColumns = ALL_COLUMNS.filter(col => selectedKeys.includes(col.key)).map(col => ({
            key: col.key,
            label: t(col.labelKey)
        }));

        const filename = `users_export_${new Date().toISOString().slice(0, 10)}`;

        if (format === "xls") {
            exportToXLS(data, activeColumns, filename, t);
            onClose();
        } else if (format === "csv") {
            exportToCSV(data, activeColumns, filename, t);
            onClose();
        } else if (format === "pdf") {
            triggerPDFPrint(data, activeColumns, filename);
            onClose();
        }
    };

    const triggerPDFPrint = (exportData, exportCols, filename) => {
        const printContainer = document.createElement("div");
        printContainer.id = "printable-users-table";
        printContainer.style.direction = "rtl";
        printContainer.style.padding = "20px";
        printContainer.style.fontFamily = "system-ui, -apple-system, sans-serif";

        const title = document.createElement("h2");
        title.innerText = t("Pages.UsersControl.export.title");
        title.style.textAlign = "center";
        title.style.marginBottom = "20px";
        printContainer.appendChild(title);

        const table = document.createElement("table");
        table.style.width = "100%";
        table.style.borderCollapse = "collapse";
        table.style.fontSize = "12px";

        // Table Header
        const thead = document.createElement("thead");
        const headerRow = document.createElement("tr");
        exportCols.forEach(col => {
            const th = document.createElement("th");
            th.innerText = col.label;
            th.style.border = "1px solid #ddd";
            th.style.padding = "8px";
            th.style.backgroundColor = "#f2f2f2";
            th.style.textAlign = "right";
            headerRow.appendChild(th);
        });
        thead.appendChild(headerRow);
        table.appendChild(thead);

        // Table Body
        const tbody = document.createElement("tbody");
        exportData.forEach(item => {
            const row = document.createElement("tr");
            exportCols.forEach(col => {
                const td = document.createElement("td");
                td.innerText = getCellValue(item, col.key, t);
                td.style.border = "1px solid #ddd";
                td.style.padding = "8px";
                td.style.textAlign = "right";
                row.appendChild(td);
            });
            tbody.appendChild(row);
        });
        table.appendChild(tbody);
        printContainer.appendChild(table);

        const style = document.createElement("style");
        style.innerText = `
            @media print {
                body > * {
                    display: none !important;
                }
                #printable-users-table {
                    display: block !important;
                }
                #printable-users-table, #printable-users-table * {
                    visibility: visible !important;
                }
            }
        `;

        document.body.appendChild(printContainer);
        document.head.appendChild(style);

        setTimeout(() => {
            window.print();
            document.body.removeChild(printContainer);
            document.head.removeChild(style);
        }, 300);
    };

    return (
        <Modal show={show} onHide={onClose} centered size="lg" className="download-modal-container">
            <Modal.Header closeButton className="border-0 pb-0">
                <Modal.Title className="w-100">
                    <div className="d-flex align-items-center justify-content-between">
                        <div className="h5 mb-0 font-weight-bold text-dark">
                            <i className="fad fa-file-export text-primary icon ml-2" style={{ color: '#92278f' }}></i>
                            {t("Pages.UsersControl.export.title")}
                        </div>
                    </div>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="text-right pt-3">
                {/* Format selection */}
                <div className="download-section-card mb-4">
                    <label className="font-weight-bold mb-3 text-muted" style={{ fontSize: '0.88rem' }}>
                        {t("Pages.UsersControl.export.format")}
                    </label>
                    <div className="d-flex gap-3 justify-content-start flex-wrap">
                        <button
                            type="button"
                            className={`download-format-btn ${format === "xls" ? "active" : ""}`}
                            onClick={() => setFormat("xls")}
                        >
                            <i className="fal fa-file-excel ml-1"></i>
                            {t("Pages.UsersControl.export.xls")}
                        </button>
                        <button
                            type="button"
                            className={`download-format-btn ${format === "csv" ? "active" : ""}`}
                            onClick={() => setFormat("csv")}
                        >
                            <i className="fal fa-file-csv ml-1"></i>
                            {t("Pages.UsersControl.export.txt")} (CSV)
                        </button>
                        <button
                            type="button"
                            className={`download-format-btn ${format === "pdf" ? "active" : ""}`}
                            onClick={() => setFormat("pdf")}
                        >
                            <i className="fal fa-file-pdf ml-1"></i>
                            {t("Pages.UsersControl.export.pdf")}
                        </button>
                    </div>
                </div>

                {/* Columns Selection */}
                <div className="download-section-card mb-1">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <label className="font-weight-bold mb-0 text-muted" style={{ fontSize: '0.88rem' }}>
                            {t("Pages.UsersControl.export.selectColumns")}
                        </label>
                        <button
                            type="button"
                            className="btn btn-link btn-sm p-0 text-decoration-none"
                            style={{ color: '#92278f', fontWeight: '600', fontSize: '0.85rem' }}
                            onClick={handleSelectAll}
                        >
                            {selectedKeys.length === ALL_COLUMNS.length ? t("hideAll") : t("showAll")}
                        </button>
                    </div>
                    
                    <div className="d-flex flex-column gap-3">
                        {COLUMN_CATEGORIES.map(category => (
                            <div key={category.id} className="category-group border p-3 rounded-lg mb-3" style={{ borderColor: '#e2e8f0', backgroundColor: '#f8fafc', borderRadius: '12px' }}>
                                <div className="category-group-header d-flex align-items-center mb-3 font-weight-bold" style={{ color: '#475569', fontSize: '0.92rem', borderBottom: '1px solid #e2e8f0', paddingBottom: '8px' }}>
                                    <i className={`${category.icon} ml-2 text-primary`} style={{ color: '#92278f', fontSize: '1.05rem' }}></i>
                                    <span>{t(category.titleKey) || category.defaultTitle}</span>
                                </div>
                                <div className="row">
                                    {category.columns.map(col => {
                                        const isChecked = selectedKeys.includes(col.key);
                                        return (
                                            <div key={col.key} className="col-md-6 mb-2">
                                                <div
                                                    className={`column-checkbox-item ${isChecked ? "checked" : ""}`}
                                                    onClick={() => handleCheckboxChange(col.key)}
                                                    style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                                                >
                                                    <div className="custom-control custom-checkbox pointer-events-none w-100 d-flex align-items-center">
                                                        <input
                                                            type="checkbox"
                                                            className="custom-control-input"
                                                            id={`col-${col.key}`}
                                                            checked={isChecked}
                                                            readOnly
                                                        />
                                                        <label className="custom-control-label mr-4 pointer-events-none d-flex align-items-center" htmlFor={`col-${col.key}`} style={{ fontSize: '0.88rem' }}>
                                                            <i className={`${col.icon} ml-2 text-muted`} style={{ width: '18px', textAlign: 'center' }}></i>
                                                            {t(col.labelKey)}
                                                        </label>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer className="border-0 pt-0 justify-content-between">
                <button type="button" className="btn btn-light px-4 py-2" style={{ borderRadius: '10px', fontWeight: '600' }} onClick={onClose}>
                    {t("Cancel")}
                </button>
                <button
                    type="button"
                    className="btn btn-primary px-4 py-2 d-flex align-items-center gap-2 shadow-sm border-0"
                    onClick={handleDownload}
                    style={{ borderRadius: '10px', fontWeight: '600' }}
                >
                    <i className="fal fa-file-download ml-2"></i>
                    {t("Pages.UsersControl.export.download")}
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default DownloadModal;
